import {sourceBrandText as text} from './sourceBrandText';

/** Logo files people can pick; each is converted to the PNG the Studio API stores. */
export const LOGO_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
];
export const MAX_LOGO_UPLOAD_BYTES = 10 * 1024 * 1024;
const MAX_PNG_BYTES = 4 * 1024 * 1024;

/** Returns a problem with the chosen file, or null when it can be converted. */
export function logoFileProblem(file: File): string | null {
  if (!LOGO_TYPES.includes(file.type)) return text.logoType;
  if (file.size > MAX_LOGO_UPLOAD_BYTES) return text.logoTooLarge;
  return null;
}

function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(text.logoUnreadable));
    image.src = url;
  });
}

/** Redraws a logo as a transparent PNG no larger than 1024px on its longest side. */
export async function logoToPng(file: Blob): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    // SVGs without an intrinsic size report 0; they scale to any size.
    const vector = file.type === 'image/svg+xml';
    const width = image.naturalWidth || 512;
    const height = image.naturalHeight || 512;
    for (const side of [1024, 512]) {
      const scale = side / Math.max(width, height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(
        1,
        Math.round(width * (vector ? scale : Math.min(1, scale))),
      );
      canvas.height = Math.max(
        1,
        Math.round(height * (vector ? scale : Math.min(1, scale))),
      );
      const context = canvas.getContext('2d');
      if (!context) throw new Error(text.logoBrowser);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const png = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (png && png.size <= MAX_PNG_BYTES) return png;
    }
    throw new Error(text.logoTooDetailed);
  } finally {
    URL.revokeObjectURL(url);
  }
}
